// Import Libraries
// Import JS Modules
import partition from 'lodash/partition';
import get from 'lodash/get';
import TemplateCache from '../templates/template-cache';
import EnvelopePage from '../misc/envelope-page';
import { usePrismItemStore } from 'stores/usePrismItemStore';

const PageSpreads = function (prismItem) {
  this.prismItem = prismItem;
};
window.PageSpreads = PageSpreads;

const Spread = function (obj) {
  Object.assign(this, obj);

  const keys = ['left', 'right'];

  this.includesPage = function (index) {
    for (const key of keys) {
      const { page } = this[key] || {};

      if (page && page.index === index) return key;
    }
  };

  this.pages = function () {
    const list = [];

    for (const key of keys) {
      const { page } = this[key] || {};

      if (page) list.push(page.index);
    }

    return list;
  };

  this.pageSize = function (data) {
    if (data && data.template && data.template.size) return data.template.size;

    const id = data.template && data.template.id;
    if (id && TemplateCache.store[id])
      return TemplateCache.store[id]['pixel-size'];

    for (const key of keys) {
      const { page } = this[key] || {};

      if (page && page.template) {
        return page.template.size || page.template['pixel-size'];
      }
    }
  };

  this.blankData = function (data) {
    return {
      slots: [],
      blank: true,
      template: {
        id: '@blank',
        prefix: 'about:blank',
        over: 'about:blank',
        back: 'about:blank',
        overlays: [],
        pso: [],
        tso: [],
        size: this.pageSize(data),
      },
    };
  };

  this.hasInvisible = function () {
    return keys.some((key) => get(this, [key, 'hint']) === 'invisible');
  };

  this.numberOfImages = function () {
    const pagesIndex = this.pages();
    const listPages = usePrismItemStore.getState().listPages();
    return listPages.reduce((total, page) => {
      if (pagesIndex.includes(page.index)) {
        return total + (page.slots || []).filter((slot) => slot.image).length;
      }
      return total;
    }, 0);
  };
};

PageSpreads.prototype.section = function (index, property) {
  if (!this.prismItem.product) {
    return;
  }

  if (!this.prismItem.product.sections) {
    return;
  }

  const section = this.prismItem.product.sections[index];
  if (property) {
    return section && section[property];
  }

  return section;
};

PageSpreads.prototype.firstSectionIsWindowCover = function () {
  const [cover] = get(this.prismItem, 'product.sections', [{ hints: [] }]);

  return cover.hints.includes('window-cover');
};

PageSpreads.prototype.hasEnvelope = function () {
  return window.wkData.values.hasEnvelopeOption;
};

PageSpreads.prototype.find = function (pageNo) {
  pageNo = parseInt(pageNo, 10);

  return this.listWithoutInvisibles().spreads.find((spread) =>
    spread.includesPage(pageNo),
  );
};

PageSpreads.prototype.pageList = function () {
  const pages = [];

  for (let i = 0; i < this.prismItem.pageCount; i++) {
    pages[i] = this.prismItem.pages[i];
  }

  return pages;
};

PageSpreads.prototype.list = function () {
  const firstSectionIsWindowCover = this.firstSectionIsWindowCover();
  const hasEnvelope = this.hasEnvelope();
  const pages = this.pageList();
  const list = [];
  let pageNum;

  if (firstSectionIsWindowCover) {
    const pg = pages.shift();
    pageNum = pg.index;

    list.push(
      new Spread({
        spread: list.length,
        left: { page: null, hint: 'invisible' },
        right: { page: { ...pg, valid: true, title: 'Cover' } },
        title: 'Cover',
      }),
    );

    list.push(
      new Spread({
        spread: list.length,
        left: { page: null, hint: 'hatch' },
        right: { page: { ...pg, valid: true }, hint: 'open-cover' },
        title: pageNum,
      }),
    );
  }

  const firstPageIndex = pages[0]?.index;
  const firstSectionTags = get(this.prismItem, 'product.sections[0].tags', []);
  const hasFirstPageTag = firstSectionTags.includes('first-page');

  while (pages.length) {
    const spread = new Spread({ spread: list.length });
    const pg = pages.shift();
    pageNum = pg.index;
    let key;

    if (wkData.product.type != 'wmcard' && pg['page-is'] == 'left') {
      let pg2;

      if (pages[0] && pages[0].index == pg['page-pair']) {
        pg2 = pages.shift();
      }

      spread.left = { page: pg, hint: 'default' };
      if (firstSectionIsWindowCover) {
        pg.title = pageNum;
      }

      if (pg2) {
        spread.right = { page: pg2, hint: 'default' };
        if (firstSectionIsWindowCover) {
          pg2.title = pageNum;
        }
      } else {
        spread.right = { hint: 'blank' };
      }
    } else {
      switch (wkData.product.type) {
        case 'album':
          key = { left: 'left', right: 'right' }[pg['page-is']] || 'left';
          spread[key] = { page: pg, hint: 'default' };

          if (firstSectionIsWindowCover) {
            pg.title = pageNum;
          }

          if (pg['page-is'] == 'left') {
            spread.right = { hint: 'blank' };
          }
          if (pg['page-is'] == 'right') {
            spread.left = { hint: 'blank' };
          }
          break;
        default:
          spread.right = { page: pg, hint: 'default' };
          break;
      }
    }

    const isInsideCoverSpread = !hasFirstPageTag && pageNum === firstPageIndex;

    [spread.left, spread.right].filter(Boolean).forEach((side) => {
      if (typeof side.page !== 'undefined') {
        side.page = { ...side.page, valid: true };
      } else {
        side.page = {
          name: isInsideCoverSpread ? 'Inside Cover' : 'Blank Page',
        };
      }

      side.page.title = (side.page.title || '').toString();
    });

    list.push(spread);
  }

  if (hasEnvelope) {
    list.push(
      new Spread({
        spread: list.length,
        left: { page: null, hint: 'invisible' },
        right: {
          page: { name: 'Envelope', title: 'Envelope', template: {} },
          customComponent: EnvelopePage,
        },
      }),
    );
  }

  list.forEach((spread) => {
    spread.title = (({ left, right }) => {
      [left, right] = [left, right].map((side) => get(side, 'page.name', ''));

      if (left.toLowerCase() === 'cover') {
        return left;
      }

      return [left, right].filter(Boolean).join(' - ').replace(/Page /g, '');
    })(spread);
  });

  return list;
};

PageSpreads.prototype.listWithoutInvisibles = function () {
  const [invisibles, spreads] = partition(this.list(), (s) => s.hasInvisible());

  return {
    spreads,
    numberOfInvisibles: invisibles.length,
  };
};

export default PageSpreads;
